<template>
  <div class="ticket-transfer-dropdown" v-on-clickaway="closeDropdown">
    <!-- Trigger: slot or default avatar button -->
    <div class="transfer-trigger-wrap" @click="toggleDropdown">
      <slot name="trigger">
        <button class="transfer-trigger" type="button">
          <thumbnail
            :src="assignedAgent && assignedAgent.thumbnail"
            :username="assignedAgent ? assignedAgent.name : '?'"
            size="28px"
          />
          <div class="trigger-info">
            <span class="trigger-name">{{ assignedAgent ? assignedAgent.name : '–' }}</span>
            <span class="trigger-role">{{ $t('TICKET.TRANSFER.OWNER_LABEL') }}</span>
          </div>
          <fluent-icon icon="chevron-down" size="12" />
        </button>
      </slot>
    </div>

    <div v-if="isOpen" class="transfer-popup" :style="popupStyle">
      <div class="popup-head">
        <thumbnail
          :src="assignedAgent && assignedAgent.thumbnail"
          :username="assignedAgent ? assignedAgent.name : '?'"
          size="32px"
        />
        <div class="popup-head-info">
          <span class="popup-agent-name">{{ assignedAgent ? assignedAgent.name : '–' }}</span>
          <span class="popup-agent-role">{{ $t('TICKET.TRANSFER.OWNER_LABEL') }}</span>
        </div>
      </div>

      <div class="popup-body">
        <div class="popup-field">
          <label class="popup-label">{{ $t('TICKET.TRANSFER.FUNNEL_LABEL') }}</label>
          <select v-model="selectedKanban" @change="onKanbanChange">
            <option :value="null">{{ currentKanbanLabel }}</option>
            <option
              v-for="kanban in activeKanbans"
              :key="kanban.id"
              :value="kanban"
            >
              {{ kanban.name }}
            </option>
          </select>
        </div>

        <div v-if="selectedKanban" class="popup-field">
          <label class="popup-label">{{ $t('TICKET.TRANSFER.COLUMN_LABEL') }}</label>
          <select v-model="selectedColumn">
            <option value="" disabled>
              {{ $t('TICKET.TRANSFER.COLUMN_PLACEHOLDER') }}
            </option>
            <option
              v-for="col in selectedKanban.columns"
              :key="col.id"
              :value="col.id"
            >
              {{ col.name }}
            </option>
          </select>
        </div>

        <div class="popup-field">
          <label class="popup-label">{{ $t('TICKET.TRANSFER.OWNERSHIP_LABEL') }}</label>
          <select v-model="selectedAgent">
            <option
              v-for="agent in availableAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}{{ agent.id === currentUser.id ? ` (${$t('TICKET.TRANSFER.YOU')})` : '' }}
            </option>
          </select>
        </div>
      </div>

      <div class="popup-footer">
        <woot-button
          type="button"
          variant="clear"
          color-scheme="secondary"
          size="small"
          @click="closeDropdown"
        >
          {{ $t('TICKET.TRANSFER.CANCEL') }}
        </woot-button>
        <woot-button
          :is-loading="isLoading"
          :disabled="!isValid || isLoading"
          size="small"
          class="btn-save-transfer"
          @click="onSubmit"
        >
          {{ $t('TICKET.TRANSFER.SAVE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';

export default {
  name: 'TicketTransferDropdown',
  components: { Thumbnail },
  mixins: [clickaway, alertMixin],
  props: {
    ticket: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isOpen: false,
      selectedKanban: null,
      selectedColumn: '',
      selectedAgent: this.ticket.user_id || '',
      isLoading: false,
      popupPos: null,
    };
  },
  computed: {
    ...mapGetters({
      kanbanList: 'getKanbanfunnels',
      agentList: 'agents/getAgents',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    assignedAgent() {
      return (this.agentList || []).find(a => a.id === this.ticket.user_id) || null;
    },
    currentKanban() {
      return (this.kanbanList || []).find(k => k.id === this.ticket.kanbanfunnel_id) || null;
    },
    currentKanbanLabel() {
      const name = this.currentKanban ? this.currentKanban.name : '–';
      return `${name} (${this.$t('TICKET.TRANSFER.CURRENT')})`;
    },
    activeKanbans() {
      const isAdmin = this.currentRole === 'administrator';
      const userId = this.currentUser?.id;
      return (this.kanbanList || []).filter(k => {
        if (!k.active || k.id === this.ticket.kanbanfunnel_id) return false;
        if (isAdmin) return true;
        return Array.isArray(k.agents_ids) && k.agents_ids.includes(userId);
      });
    },
    availableAgents() {
      const kanban = this.selectedKanban || this.currentKanban;
      if (!kanban) return this.agentList || [];
      const ids = kanban.agents_ids || [];
      return (this.agentList || []).filter(a => ids.includes(a.id));
    },
    isValid() {
      if (!this.selectedAgent) return false;
      if (this.selectedKanban) return !!this.selectedColumn;
      return true;
    },
    popupStyle() {
      if (!this.popupPos) return {};
      return {
        position: 'fixed',
        top: this.popupPos.top + 'px',
        right: this.popupPos.right + 'px',
        left: 'auto',
      };
    },
  },
  watch: {
    'ticket.user_id'(val) {
      this.selectedAgent = val || '';
    },
  },
  mounted() {
    this.$store.dispatch('fetchKanbanfunnels');
    this.$store.dispatch('agents/get');
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$emit('open');
        this.reset();
        this.$nextTick(() => {
          const wrap = this.$el.querySelector('.transfer-trigger-wrap');
          if (wrap) {
            const rect = wrap.getBoundingClientRect();
            this.popupPos = {
              top: rect.bottom + 4,
              right: window.innerWidth - rect.right,
            };
          }
        });
      }
    },
    closeDropdown() {
      this.isOpen = false;
    },
    reset() {
      this.selectedKanban = null;
      this.selectedColumn = '';
      this.selectedAgent = this.ticket.user_id || '';
    },
    onKanbanChange() {
      this.selectedColumn = '';
      // reset agent to current user if they're in the new kanban, otherwise first agent
      const ids = this.selectedKanban?.agents_ids || [];
      if (ids.includes(this.ticket.user_id)) {
        this.selectedAgent = this.ticket.user_id;
      } else if (ids.length > 0) {
        const first = (this.agentList || []).find(a => ids.includes(a.id));
        this.selectedAgent = first ? first.id : '';
      } else {
        this.selectedAgent = '';
      }
    },
    async onSubmit() {
      if (!this.isValid) return;
      this.isLoading = true;
      try {
        let payload;
        if (this.selectedKanban) {
          payload = {
            id: this.ticket.id,
            timestamp: this.ticket.timestamp,
            kanbanfunnel_id: this.selectedKanban.id,
            columns_id: this.selectedColumn,
            user_id: this.selectedAgent,
          };
        } else {
          payload = {
            id: this.ticket.id,
            timestamp: this.ticket.timestamp,
            user_id: this.selectedAgent,
          };
        }
        const response = await this.$store.dispatch('updateTicket', payload);
        const successKey = this.selectedKanban
          ? 'TICKET.TRANSFER.SUCCESS_KANBAN'
          : 'TICKET.TRANSFER.SUCCESS_AGENT';
        this.showAlert(this.$t(successKey));
        this.closeDropdown();
        const updated = { ...this.ticket, ...payload, ...response };
        this.$emit('success', updated);
      } catch {
        this.showAlert(this.$t('TICKET.TRANSFER.ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ticket-transfer-dropdown {
  position: relative;
  display: inline-flex;
}

.transfer-trigger-wrap {
  display: inline-flex;
  cursor: pointer;
}

.transfer-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--border-radius-normal);
  color: var(--color-body);

  &:hover {
    background: var(--color-background-light);
  }
}

.trigger-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.trigger-name {
  font-size: 1.3rem;
  font-weight: var(--font-weight-medium);
}

.trigger-role {
  font-size: 1.1rem;
  color: var(--color-light-gray);
}

.transfer-popup {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 9999;
  background: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  box-shadow: var(--shadow-dropdown-pane);
  min-width: 220px;
  padding: 12px;

  /* when fixed positioning is applied via :style, these are overridden */
  &[style] {
    top: unset;
    right: unset;
  }
}

.popup-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border-light);
}

.popup-head-info {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.popup-agent-name {
  font-size: 1.3rem;
  font-weight: var(--font-weight-medium);
}

.popup-agent-role {
  font-size: 1.1rem;
  color: var(--color-light-gray);
}

.popup-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.popup-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.popup-label {
  font-size: 1.2rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-heading);
  margin: 0;
}

select {
  width: 100%;
  margin-bottom: 0;
  font-size: 1.3rem;
}

.popup-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--color-border-light);
}

::v-deep .btn-save-transfer {
  background: #44ce4b !important;
  border-color: #44ce4b !important;
  color: #fff !important;

  &:hover:not(:disabled) {
    background: #3ab840 !important;
    border-color: #3ab840 !important;
  }
}
</style>

